// Portals-4 (2026-06-21) — Landing page hit by the accept-invite email link.
// Calls POST /api/portal/accept-invite/{token} on mount. On success
// the recipient is bounced into the Client Portal with the new entity
// pre-selected via localStorage. On failure they see the error + a link
// back to login.
//
// Note: the endpoint is public — the backend keys off the token itself, so
// no login is required to land here. If the user *isn't* logged in we still
// flip them to /login (with a notice) once we've consumed the token —
// the Client Portal route is gated on role=user.
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, AlertTriangle, Loader2 } from "lucide-react";
import { setActiveEntityId } from "@/components/client/EntitySwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", message: "", entity: null });

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) {
        setState({ status: "error", message: "Missing invite token." });
        return;
      }
      try {
        // HF-404-1 (2026-07-31): this called `/client-entities/accept-invite/`,
        // but routes/client_entities.py registers the endpoint as
        // `/portal/accept-invite/{token}` (client_entities.py:470) and its
        // router is mounted bare under /api. Every invite email landed on this
        // page and then 404'd. The path below matches the `accept_url` the
        // backend itself builds for the email (client_entities.py:343, :453).
        const res = await axios.post(`${API}/portal/accept-invite/${token}`);
        if (cancelled) return;
        const entity = res.data?.entity;
        if (entity?.id) {
          setActiveEntityId(entity.id);
        }
        setState({
          status: "ok",
          message: res.data?.needs_password_setup
            ? "Invite accepted — sign in or create your password to continue."
            : "Invite accepted — taking you to your portal.",
          entity,
          needs_password_setup: !!res.data?.needs_password_setup,
        });
        // If the user already has an account + is logged in, the portal
        // route will pick up the new active entity. Otherwise the login
        // page will route them after sign-in.
        const isLoggedIn = !!localStorage.getItem("token");
        const target = isLoggedIn && !res.data?.needs_password_setup ? "/portal" : "/login";
        setTimeout(() => { if (!cancelled) navigate(target); }, 1500);
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          message: err.response?.data?.detail || "This invite link is no longer valid.",
        });
      }
    }
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-sm p-8 text-center">
        <h1 className="text-lg font-bold text-[#bd9a59] mb-1">SOLARA GLOBAL</h1>
        <p className="text-xs text-gray-400 mb-6 uppercase tracking-wide">Client portal invite</p>

        {state.status === "loading" && (
          <div className="flex flex-col items-center gap-3 py-6">
            <Loader2 className="w-8 h-8 text-[#bd9a59] animate-spin" />
            <p className="text-sm text-gray-500">Accepting your invite…</p>
          </div>
        )}

        {state.status === "ok" && (
          <div className="flex flex-col items-center gap-3 py-4">
            <CheckCircle className="w-10 h-10 text-green-500" />
            <p className="text-sm text-[#021d49] font-medium">
              You've joined {state.entity?.entity_name || "the entity"}.
            </p>
            <p className="text-xs text-gray-500">{state.message}</p>
          </div>
        )}

        {state.status === "error" && (
          <div className="flex flex-col items-center gap-3 py-4">
            <AlertTriangle className="w-10 h-10 text-amber-500" />
            <p className="text-sm text-[#021d49] font-medium">Invite couldn't be accepted</p>
            <p className="text-xs text-gray-500">{state.message}</p>
            <Link to="/login" className="mt-2 text-xs text-[#bd9a59] hover:underline">
              Back to login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
